import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  type OnInit,
  Output,
  effect,
  inject,
  viewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatExpansionPanel } from '@angular/material/expansion';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '../../../authentication/_services/auth.service';
import { type MenuItem, MenuService } from '../../services/menu.service';
import { TranslationService } from '../../services/translation.service';
import { PasswordExpiryService } from '../password-expiry-modal/services/password-expiry.service.component';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SideNavComponent implements OnInit, AfterViewInit {
  private readonly menuService = inject(MenuService);
  private readonly translationService = inject(TranslationService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly passwordExpiryService = inject(PasswordExpiryService);

  menuItems: MenuItem[] = [];

  readonly panels = viewChildren(MatExpansionPanel);
  @Input() isSidenavOpen = false;
  @Output() sidenavToggle = new EventEmitter<void>();

  constructor() {
    effect(() => {
      const lang = this.translationService.languageChanged();
      if (lang) {
        this.loadMenuItems();
      }
    });
  }

  ngOnInit(): void {
    this.authService.userInfo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: userInfo => {
        if (userInfo && userInfo.userRoles && userInfo.userRoles.length > 0) {
          this.loadMenuItems();
        }
      },
      error: (err: unknown) => console.error('User info change error:', err)
    });
  }

  ngAfterViewInit(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.expandActiveParent());
    this.expandActiveParent();
  }

  private expandActiveParent(): void {
    const panelList = this.panels();
    if (!panelList.length) {
      return;
    }
    const url = this.router.url.split(/[?#]/)[0];
    const parents = this.menuItems.filter(item => item.children?.length);
    panelList.forEach((panel, index) => {
      const children = parents[index]?.children ?? [];
      const hasActiveChild = children.some(child => !!child.link && (url === child.link || url.startsWith(`${child.link}/`)));
      if (hasActiveChild) {
        panel.open();
      }
    });
    this.cdr.markForCheck();
  }

  loadMenuItems(): void {
    this.menuService
      .getMenuItems()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => {
          this.menuItems = this.menuService.filterMenuItemsByRole(items, this.authService.getUserRoles());
          this.cdr.markForCheck();
        },
        error: () => {
          this.menuItems = [];
          this.cdr.markForCheck();
        }
      });
  }

  toggleSidenav(): void {
    this.sidenavToggle.emit();
  }
}
